
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// module components
import PretensionForm from '@/modules/PretensionsModule/components/PretensionForm/index.vue'

// types
import {
    GuiltySide,
    PretensionsDownloadPretensionPayloadT,
    PretensionsDownloadPretensionResponseT,
    PretensionStatusT,
    PretensionT,
    PretensionTypeT,
} from '@/core/types/pretensions.types'

// realisations
import { pretensions } from '@/core/realisations/pretensions.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// constants
import { PretensionsColumns } from '@/core/constants/columns/Pretensions.columns'
import { companies } from '@/core/realisations/companies.realisation'

// composable
import cut from '@/common/helpers/cut'

const HAS_COMPENSATION_OPTIONS = [
    { id: 1, name: 'Да', value: '1' },
    { id: 2, name: 'Нет', value: '0' },
]

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
        'failed-fetch': FailedFetch,
        'pretension-form': PretensionForm,
        TheTable,
        TableScrollWrapper,
        Cheap,
    },
    setup() {
        const state = reactive({
            pretensions: new DataState<PretensionT>({}),
            subdivisions: new DataState<any>({}),
            pretensions_types: [] as PretensionTypeT[] | null,
            pretensions_statuses: [] as PretensionStatusT[] | null,
            guilty_sides: new DataState<GuiltySide>({}),
            is_loading: false,
        })

        const form_state = reactive({
            is_update: false,
            is_create: false,
            is_visible: false,
            initial_value: null as PretensionT | null,
        })

        const filters_state = reactive({
            limit: 50,
            //
            id: undefined as number | undefined,
            invoice_number: undefined as string | undefined,
            company_name: undefined as string | undefined,
            client_name: undefined as string | undefined,
            product_description: undefined as string | undefined,
            type_id: null as string | null,
            status_code: null as string | null,
            from_date: null as string | null,
            to_date: null as string | null,
            subdivision_id: null as number | null,
            guilty_side_id: null as number | null,
            has_compensation: null as string | null,
        })

        const loadPretensions = async (page: number) => {
            state.pretensions.pagination.current_page = page

            let has_compensation = null

            if (filters_state.has_compensation === '1') {
                has_compensation = 1
            }

            if (filters_state.has_compensation === '0') {
                has_compensation = 0
            }

            // filters
            const params = {
                page,
                type_id: filters_state.type_id ? Number(filters_state.type_id) : undefined,
                company_name: filters_state.company_name,
                id: filters_state.id,
                invoice_number: filters_state.invoice_number,
                product_description: filters_state.product_description,
                client_name: filters_state.client_name,
                status_code: filters_state.status_code,
                from_date: filters_state.from_date,
                to_date: filters_state.to_date,
                subdivision_id: filters_state.subdivision_id,
                guilty_side_id: filters_state.guilty_side_id,
                limit: filters_state.limit,
                has_compensation,
            }

            try {
                state.pretensions.is_loading = true

                const response = await pretensions.getList(params)
                await pretensions
                    .getTypes(page)
                    .then((resp) => (state.pretensions_types = resp.data))
                await pretensions
                    .getStatuses(page)
                    .then((resp) => (state.pretensions_statuses = resp.data))

                state.pretensions.list = response.data
                state.pretensions.pagination.total_pages = response.meta.last_page
                state.pretensions.pagination.total_items = response.meta.total

                state.pretensions.is_failed = false
                state.pretensions.failure.error_state.is_cancel = false
            } catch (error) {
                state.pretensions.failure.catchError(error)

                if (!state.pretensions.failure.error_state.is_cancel) {
                    state.pretensions.is_failed = true
                }
            } finally {
                if (!state.pretensions.failure.error_state.is_cancel) {
                    state.pretensions.is_loading = false
                }
            }
        }

        const generateExcelFromResponse = (response: PretensionsDownloadPretensionResponseT) => {
            const download_url = window.URL.createObjectURL(response)

            const link = document.createElement('a')
            link.href = download_url
            link.target = '_blank'
            link.setAttribute('download', 'Список претензий.xlsx')
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const exportExcel = async () => {
            try {
                state.is_loading = true

                let payload: PretensionsDownloadPretensionPayloadT = {
                    id: filters_state.id,
                    invoice_number: filters_state.invoice_number,
                    company_name: filters_state.company_name,
                    client_name: filters_state.client_name,
                    type_id: Number(filters_state.type_id),
                    status_code: filters_state.status_code,
                    from_date: filters_state.from_date,
                    to_date: filters_state.to_date,
                    subdivision_id: filters_state.subdivision_id,
                    guilty_side_id: filters_state.guilty_side_id,
                    has_compensation: Number(filters_state.has_compensation),
                }

                const response = await pretensions.exportExcel(payload)
                generateExcelFromResponse(response)
            } catch (e) {
                console.log(e)
            } finally {
                state.is_loading = false
            }
        }

        const resetFilters = () => {
            filters_state.type_id = null
            filters_state.company_name = undefined
            filters_state.id = undefined
            filters_state.invoice_number = undefined
            filters_state.product_description = undefined
            filters_state.client_name = undefined
            filters_state.status_code = null
            filters_state.from_date = null
            filters_state.to_date = null
            filters_state.subdivision_id = null
            filters_state.guilty_side_id = null
            filters_state.has_compensation = null

            loadPretensions(1)
        }

        const onUpdatePretension = (pretension: PretensionT) => {
            form_state.is_update = true
            form_state.is_create = false

            form_state.initial_value = pretension
            form_state.is_visible = true
        }

        const onAddPretension = () => {
            form_state.is_update = false
            form_state.is_create = true

            form_state.initial_value = null
            form_state.is_visible = true
        }

        const closePretensionForm = () => {
            form_state.is_visible = false
            loadPretensions(state.pretensions.pagination.current_page)
        }

        const cutName = (name: string): string => {
            let max_length = 12

            if (name && name.length > max_length) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, max_length - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name || '-'
        }

        const loadSubdivisions = async () => {
            try {
                state.subdivisions.is_loading = true
                const response = await companies.getSubdivisions({ page: 1 })
                state.subdivisions.list = response.data

                state.subdivisions.is_failed = false
                state.subdivisions.failure.error_state.is_cancel = false
            } catch (error) {
                state.subdivisions.failure.catchError(error)

                if (!state.subdivisions.failure.error_state.is_cancel) {
                    state.subdivisions.is_failed = true
                }
            } finally {
                if (!state.subdivisions.failure.error_state.is_cancel) {
                    state.subdivisions.is_loading = false
                }
            }
        }

        const loadGuiltySides = async () => {
            state.guilty_sides.pagination.current_page = 1

            try {
                state.guilty_sides.is_loading = true

                const response = await pretensions.getGuiltySides()
                state.guilty_sides.list = response.data
                // data_state.guilty_sides.pagination.total_pages = response.meta.last_page

                state.guilty_sides.is_failed = false
                state.guilty_sides.failure.error_state.is_cancel = false
            } catch (error) {
                state.guilty_sides.failure.catchError(error)

                if (!state.guilty_sides.failure.error_state.is_cancel) {
                    state.guilty_sides.is_failed = true
                }
            } finally {
                if (!state.guilty_sides.failure.error_state.is_cancel) {
                    state.guilty_sides.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadPretensions(state.pretensions.pagination.current_page)
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            const dateSecond = dateStr.split(' ')[1]
            return dateFirst.split('-').reverse().join('.') + ' ' + dateSecond
        }

        onBeforeMount(() => {
            loadPretensions(1)
            loadSubdivisions()
            loadGuiltySides()
        })

        return {
            HAS_COMPENSATION_OPTIONS,
            state,
            form_state,
            filters_state,
            loadPretensions,
            loadGuiltySides,
            resetFilters,
            onUpdatePretension,
            onAddPretension,
            closePretensionForm,
            exportExcel,
            cutName,
            onLimitChange,
            loadSubdivisions,
            cut,
            formatDate,
            //
            PretensionsColumns,
        }
    },
})
